import { updateProfile } from "@/app/actions/auth/profile";
import { createClient } from "@/lib/supabase/client";
import { Code, DataList, Flex, Grid } from "@radix-ui/themes";
import { User } from "@supabase/supabase-js";
import { AlertTriangle, Loader2 } from "lucide-react";
import Link from "next/link";
import { useActionState, useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

import AvatarInitials from "../shared/AvatarInitials";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import ChangeAvatar from "./ChangeAvatar";

type StateType = {
  error?: string | undefined;
  success: boolean;
  user?: any;
};

export default function ChangeProfile({
  user,
  updateUser,
}: {
  user?: User | null;
  updateUser: () => void;
}) {
  const supabase = createClient();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [open, setOpen] = useState(false);

  const [avatar, _setAvatar] = useState<File | null>(null);
  const [_preview, setPreview] = useState<string | null>(null);

  const [firstName, setFirstName] = useState<string | undefined>(
    user?.user_metadata?.first_name,
  );
  const [lastName, setLastName] = useState<string | undefined>(
    user?.user_metadata?.last_name,
  );
  const [website, setWebsite] = useState<string | undefined>(
    user?.user_metadata?.website,
  );

  const checkMfa = async () => {
    const { data, error } =
      await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) {
      throw error;
    }

    const { data: totp, error: totperror } =
      await supabase.auth.mfa.listFactors();
    if (totperror) {
      throw totperror;
    }

    if (data.nextLevel === "aal2" && totp && totp.totp.length > 0) {
      setMfaEnabled(true);
    } else {
      setMfaEnabled(false);
    }
  };

  useEffect(() => {
    (async () => checkMfa())();
  }, []);

  useEffect(() => {
    if (avatar) {
      // create the preview
      const objectUrl = URL.createObjectURL(avatar);
      setPreview(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [avatar]);

  // const updateAvatar = useCallback(async () => {
  //   if (!avatar) {
  //     return;
  //   }

  //   const { error } = await UpdateAvatar(avatar);

  //   if (error) {
  //     return {
  //       error: error,
  //     };
  //   }

  //   updateUser();
  // }, [avatar, updateUser]);

  const [profileState, profileSubmitAction, profileIsPending] = useActionState(
    async (_previousState: any, formData: FormData): Promise<StateType> => {
      const firstName = formData.get("firstName");
      const lastName = formData.get("lastName");
      const website = formData.get("website");

      if (!firstName && !lastName && !website) {
        return {
          error: "First name, last name and website are required",
          success: false,
        };
      }

      const { data, error } = await updateProfile({
        firstName: firstName?.toString(),
        lastName: lastName?.toString(),
        website: website?.toString(),
      });

      if (error) {
        return {
          error: error.message,
          success: false,
        };
      }
      handleDialog(false);
      updateUser();
      return {
        success: true,
        user: data,
      };
    },
    null,
  );

  const checkError = (state: StateType | null): boolean => {
    if (!state) {
      return false;
    }
    if (state.success) {
      return false;
    }

    return true;
  };

  const handleDialog = (state: boolean) => {
    if (state) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  // const updateAvatar = async () => {
  //   if (!avatar) {
  //     return;
  //   }

  //   const { data, error } = await updateAvatar(avatar);

  //   if (error) {
  //     return {
  //       error: error.message,
  //     };
  //   }

  //   updateSession();
  // };

  return (
    <Card id="editProfile">
      <CardHeader>
        <CardTitle>{user?.email}</CardTitle>
        <CardDescription>A brief overview of your account</CardDescription>
      </CardHeader>
      <CardContent>
        <DataList.Root>
          <DataList.Item>
            <DataList.Label minWidth="88px">ID</DataList.Label>
            <DataList.Value>
              <Flex align="center" gap="2">
                <Code variant="ghost">{user?.id}</Code>
              </Flex>
            </DataList.Value>
          </DataList.Item>
          <DataList.Item align="center">
            <DataList.Label minWidth="88px">
              Multi Factor Authentication
            </DataList.Label>
            <DataList.Value>
              {mfaEnabled ? (
                <Badge color="green" variant="default">
                  Enabled
                </Badge>
              ) : (
                <Badge color="red" variant="default">
                  Disabled
                </Badge>
              )}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label minWidth="88px">Email Verified</DataList.Label>
            <DataList.Value>
              {user?.email_confirmed_at ? (
                <Badge color="green" variant="default">
                  Yes
                </Badge>
              ) : (
                <Badge color="red" variant="default">
                  No
                </Badge>
              )}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label minWidth="88px">Avatar</DataList.Label>
            <DataList.Value>
              <ChangeAvatar user={user} updateUser={updateUser}>
                <AvatarInitials
                  firstName={user?.user_metadata?.full_name}
                  lastName={user?.user_metadata?.last_name}
                  image={user?.user_metadata?.avatar_url}
                />
              </ChangeAvatar>
            </DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label minWidth="88px">First Name</DataList.Label>
            <DataList.Value>{user?.user_metadata?.first_name}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label minWidth="88px">Last Name</DataList.Label>
            <DataList.Value>{user?.user_metadata?.last_name}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label minWidth="88px">Website</DataList.Label>
            <DataList.Value>{user?.user_metadata?.website}</DataList.Value>
          </DataList.Item>
        </DataList.Root>
      </CardContent>
      <CardFooter className="border-t px-6 py-4">
        <Dialog open={open} onOpenChange={(val) => handleDialog(val)}>
          <DialogTrigger asChild>
            <Button variant="default" type="button" className="mr-2">
              Edit Profile
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form action={profileSubmitAction}>
              <DialogHeader>
                <DialogTitle>Edit Profile</DialogTitle>
              </DialogHeader>
              {checkError(profileState) && (
                <Alert variant="destructive" className="mb-4 mt-4">
                  <AlertTriangle className="h-6 w-6" />
                  <AlertTitle className="!pl-8">Error</AlertTitle>
                  <AlertDescription className="!pl-8">
                    {profileState?.error}
                  </AlertDescription>
                </Alert>
              )}
              <Flex direction="column" className="mt-2">
                <Grid className="w-full gap-1.5" align="center">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    type="text"
                    id="firstName"
                    name="firstName"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid className="w-full gap-1.5 mt-4" align="center">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    type="text"
                    id="lastName"
                    name="lastName"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>
                <Grid className="w-full gap-1.5 mt-4" align="center">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    type="text"
                    id="website"
                    name="website"
                    placeholder="Website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </Grid>
              </Flex>
              <DialogFooter className="mt-4">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => handleDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  type="submit"
                  className="ml-2"
                  disabled={profileIsPending}
                >
                  {profileIsPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Edit Profile
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Link href="/logout">
          <Button variant="outline">Logout</Button>
        </Link>
      </CardFooter>
    </Card>
  );
}
